import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

// /admin/knowledge merged into /admin/system; keep old links (incl. ?slug=) working.
const KnowledgeRedirect = () => {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  if (!p.get("tab")) p.set("tab", p.get("slug") ? "architecture" : "invariants");
  return <Navigate to={`/admin/system?${p.toString()}`} replace />;
};
import { AuthProvider } from "@/hooks/useAuth";
import { BulkImportProvider } from "@/contexts/BulkImportContext";
import { COAProcessingProvider } from "@/contexts/COAProcessingContext";
import { NavigationHistoryProvider } from "@/contexts/NavigationHistoryContext";
import { FeatureRegistryProvider } from "@/contexts/FeatureRegistryContext";
import PushAckBridge from "@/components/notifications/PushAckBridge";
import { ScrollManager } from "@/components/ScrollManager";
import { AskCannafaxProvider } from "@/contexts/AskCannafaxContext";
import BulkImportBanner from "@/components/BulkImportBanner";
import PendingInvitationBanner from "@/components/PendingInvitationBanner";
import COAProcessingBanner from "@/components/COAProcessingBanner";
import COAConfirmDialog from "@/components/coa/COAConfirmDialog";
import { lazy, Suspense } from "react";
import LazyLoadErrorBoundary from "@/components/LazyLoadErrorBoundary";
import FeedbackButton from "@/components/FeedbackButton";
import AskCannafaxWidget from "@/components/AskCannafaxWidget";
import { captureCampaignVisit } from "@/lib/campaignAttribution";
import { GoogleAnalyticsTracker } from "@/lib/googleAnalytics";

if (typeof window !== "undefined") captureCampaignVisit();
import AuthLoadingSkeleton from "@/components/AuthLoadingSkeleton";
import { useDeferredToast } from "@/hooks/useDeferredToast";
import { syncEventCatalogOnce } from "@/lib/syncEventCatalog";

// Sync analytics event taxonomy to Postgres registry (idempotent, fire-and-forget).
syncEventCatalogOnce();

// ProtectedRoute is eagerly imported to avoid auth-guard delays
import ProtectedRoute from "./components/ProtectedRoute";

// Persona landing pages are lazy-loaded but hover-prefetched via AppHeader
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MyMenu = lazy(() => import("./pages/MyMenu"));
const SupplierMenus = lazy(() => import("./pages/SupplierMenus"));
const ShareLinks = lazy(() => import("./pages/ShareLinks"));
const YourDesk = lazy(() => import("./pages/YourDesk"));
import LegacyDeskRedirect from "./components/LegacyDeskRedirect";
const Watchlist = lazy(() => import("./pages/Watchlist"));
const BuyRequests = lazy(() => import("./pages/BuyRequests"));


import Home from "./pages/Home";
import Auth from "./pages/Auth";
const Onboarding = lazy(() => import("./pages/Onboarding"));
const CompanySettings = lazy(() => import("./pages/CompanySettings"));
const ProfileSettings = lazy(() => import("./pages/ProfileSettings"));
const EmailPreferences = lazy(() => import("./pages/EmailPreferences"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const BriefFrequency = lazy(() => import("./pages/BriefFrequency"));
const ListingEdit = lazy(() => import("./pages/ListingEdit"));
const ListingView = lazy(() => import("./pages/ListingView"));
const SharedListing = lazy(() => import("./pages/SharedListing"));
const SharedLink = lazy(() => import("./pages/SharedLink"));
const Messages = lazy(() => import("./pages/Messages"));
const CompanyMenuView = lazy(() => import("./pages/CompanyMenuView"));
const Referrals = lazy(() => import("./pages/Referrals"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const BuyerInviteLanding = lazy(() => import("./pages/BuyerInviteLanding"));
const MenuView = lazy(() => import("./pages/MenuView"));
const MenuListingView = lazy(() => import("./pages/MenuListingView"));
const MenuDetail = lazy(() => import("./pages/MenuDetail"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const CustomerAgreement = lazy(() => import("./pages/CustomerAgreement"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Billing = lazy(() => import("./pages/Billing"));
const Support = lazy(() => import("./pages/Support"));
const GoogleDriveReturn = lazy(() => import("./pages/oauth/GoogleDriveReturn"));
const GoogleMailReturn = lazy(() => import("./pages/oauth/GoogleMailReturn"));
const MicrosoftReturn = lazy(() => import("./pages/oauth/MicrosoftReturn"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MenuInviteLanding = lazy(() => import("./pages/MenuInviteLanding"));
const MenuAccessRequestStatus = lazy(() => import("./pages/MenuAccessRequestStatus"));
// Admin pages
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminCompanies = lazy(() => import("./pages/admin/AdminCompanies"));
const AdminCompanyDetail = lazy(() => import("./pages/admin/AdminCompanyDetail"));
const AdminSystem = lazy(() => import("./pages/admin/AdminSystem"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));
const AdminPlaceholder = lazy(() => import("./pages/admin/AdminPlaceholder"));
const AdminPlatformAnalytics = lazy(() => import("./pages/admin/AdminPlatformAnalytics"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const AdminSupport = lazy(() => import("./pages/admin/AdminSupport"));
const AdminShareLinks = lazy(() => import("./pages/admin/AdminShareLinks"));
const AdminJurisdictions = lazy(() => import("./pages/admin/AdminJurisdictions"));
const AdminNextMoveRules = lazy(() => import("./pages/admin/AdminNextMoveRules"));
const AdminProductTypes = lazy(() => import("./pages/admin/AdminProductTypes"));
const AdminActivityCatalog = lazy(() => import("./pages/admin/AdminActivityCatalog"));
const AdminEntitlements = lazy(() => import("./pages/admin/AdminEntitlements"));
const AdminAttribution = lazy(() => import("./pages/admin/AdminAttribution"));
const queryClient = new QueryClient();

/** Displays any toast queued before a hard page redirect (e.g. from Auth.tsx). */
function DeferredToastHandler() {
  useDeferredToast();
  return null;
}

/** Suspense fallback that preserves the "Signing you in…" message across route transitions. */
function SignInAwareFallback() {
  const isSigningIn = sessionStorage.getItem('signing_in') === '1';
  return <AuthLoadingSkeleton variant={isSigningIn ? 'signing-in' : 'loading'} />;
}

const App = () => <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <FeatureRegistryProvider>
      <BulkImportProvider>
      <COAProcessingProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <AskCannafaxProvider>
          <NavigationHistoryProvider>
          <ScrollManager />
          <GoogleAnalyticsTracker />
          <DeferredToastHandler />
          <PushAckBridge />
          <BulkImportBanner />
          <PendingInvitationBanner />
          <COAProcessingBanner />
          <COAConfirmDialog />
          <LazyLoadErrorBoundary>
          <Routes>
            {/* Public routes — no branded skeleton fallback */}
            <Route path="/" element={<Home />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/terms" element={<Suspense fallback={null}><Terms /></Suspense>} />
            <Route path="/customer-agreement" element={<Suspense fallback={null}><CustomerAgreement /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={null}><Privacy /></Suspense>} />
            <Route path="/refund-policy" element={<Suspense fallback={null}><RefundPolicy /></Suspense>} />
            <Route path="/pricing" element={<Suspense fallback={null}><Pricing /></Suspense>} />
            <Route path="/support" element={<Suspense fallback={null}><Support /></Suspense>} />
            <Route path="/oauth/google-drive/return" element={<Suspense fallback={null}><GoogleDriveReturn /></Suspense>} />
            <Route path="/oauth/google-mail/return" element={<Suspense fallback={null}><GoogleMailReturn /></Suspense>} />
            <Route path="/oauth/microsoft-return" element={<Suspense fallback={null}><MicrosoftReturn /></Suspense>} />
            <Route path="/share/:id" element={<Suspense fallback={null}><SharedListing /></Suspense>} />
            <Route path="/share" element={<Suspense fallback={null}><SharedListing /></Suspense>} />
            <Route path="/shared/:id" element={<Suspense fallback={null}><SharedListing /></Suspense>} />
            <Route path="/s/:slug" element={<Suspense fallback={null}><SharedLink /></Suspense>} />
            <Route path="/menu-invite/:token" element={<Suspense fallback={null}><MenuInviteLanding /></Suspense>} />
            <Route path="/menu-access-request/:requestId" element={<Suspense fallback={null}><MenuAccessRequestStatus /></Suspense>} />
            <Route path="/unsubscribe" element={<Suspense fallback={null}><Unsubscribe /></Suspense>} />
            <Route path="/brief-frequency" element={<Suspense fallback={null}><BriefFrequency /></Suspense>} />
            <Route path="/r/:code" element={<Suspense fallback={null}><ReferralLanding /></Suspense>} />
            <Route path="/j/:code" element={<Suspense fallback={null}><BuyerInviteLanding /></Suspense>} />
            

            {/* Protected routes — branded skeleton during chunk loads */}
            <Route element={<Suspense fallback={<SignInAwareFallback />}><ProtectedRoute /></Suspense>}>
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/my-listings" element={<Dashboard />} />
              <Route path="/listings/new" element={<ListingEdit />} />
              <Route path="/listings/:id" element={<ListingView />} />
              <Route path="/listings/:id/edit" element={<ListingEdit />} />
              {/* My Insights retired: performance questions go to Your Desk / Ask Cannafax. Old links redirect. */}
              <Route path="/my-insights" element={<Navigate to="/desk" replace />} />
              <Route path="/share-links" element={<ShareLinks />} />
              <Route path="/desk" element={<YourDesk />} />
              <Route path="/my-day" element={<LegacyDeskRedirect />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/my-menu" element={<MyMenu />} />
              <Route path="/my-menu/:menuId" element={<MenuDetail />} />
              <Route path="/supplier-menus" element={<SupplierMenus />} />
              <Route path="/watchlist" element={<Watchlist />} />
              <Route path="/buy-requests" element={<BuyRequests />} />
              <Route path="/company/:companyId" element={<CompanyMenuView />} />
              <Route path="/menu/:companyId" element={<CompanyMenuView />} />
              <Route path="/menu/:companyId/:menuId" element={<MenuView />} />
              <Route path="/menu/:companyId/:menuId/listing/:listingId" element={<MenuListingView />} />
              <Route path="/menus/:menuId" element={<MenuDetail />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/referrals" element={<Referrals />} />
              <Route path="/company-settings" element={<CompanySettings />} />
              <Route path="/profile-settings" element={<ProfileSettings />} />
              <Route path="/settings/email" element={<EmailPreferences />} />
            </Route>

            {/* Admin routes — AdminLayout has its own auth gate */}
            <Route path="/admin" element={<Suspense fallback={<SignInAwareFallback />}><AdminLayout /></Suspense>}>
              <Route index element={<AdminDashboard />} />
              <Route path="companies" element={<AdminCompanies />} />
              <Route path="companies/:companyId" element={<AdminCompanyDetail />} />
              <Route path="platform-analytics" element={<AdminPlatformAnalytics />} />
              <Route path="knowledge" element={<KnowledgeRedirect />} />
              <Route path="system" element={<AdminSystem />} />
              <Route path="marketing" element={<AdminMarketing />} />
              <Route path="feedback" element={<AdminFeedback />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="share-links" element={<AdminShareLinks />} />
              <Route path="jurisdictions" element={<AdminJurisdictions />} />
              <Route path="next-move-rules" element={<AdminNextMoveRules />} />
              <Route path="product-types" element={<AdminProductTypes />} />
              <Route path="activity-catalog" element={<AdminActivityCatalog />} />
              <Route path="entitlements" element={<AdminEntitlements />} />
              <Route path="attribution" element={<AdminAttribution />} />
              <Route path="*" element={<AdminPlaceholder />} />
            </Route>

            <Route path="*" element={<Suspense fallback={null}><NotFound /></Suspense>} />
          </Routes>
          </LazyLoadErrorBoundary>
          <FeedbackButton />
          <AskCannafaxWidget />
          </NavigationHistoryProvider>
          </AskCannafaxProvider>
        </BrowserRouter>
      </TooltipProvider>
      </COAProcessingProvider>
      </BulkImportProvider>
      </FeatureRegistryProvider>
    </AuthProvider>
  </QueryClientProvider>;
export default App;
